import { computed, ComputedRef } from "vue";
import { storeToRefs } from "pinia";

import { useAuthStore } from "@/stores/Auth";
import { useBonusReportsStore } from "@/stores/BonusReports";

import { useCategoryFilter } from "@/composables/filters/category-filter";
import { usePagingFilter } from "@/composables/filters/paging-filter";
import { useProductFilter } from "@/composables/filters/product-filter";
import { useTextFilter } from "@/composables/filters/text-filter";

import { BonusReport, Tag } from "@tmi/types/entities";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
import { consoleLoggerFactory, getDefaultLoggerOptions, ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export function useBonusReportFilters() {
    const { isAuthenticated } = storeToRefs( useAuthStore() );
    const bonusReportsStore = useBonusReportsStore();
    const { allBonusReports } = storeToRefs( bonusReportsStore );

    // -------------------------------- FILTER STATE --------------------------------- //
    const hasAnyBonusReportsResults: ComputedRef< boolean > = computed( (): boolean => filteredBonusReports.value.length > 0 );
    const searchResultsLabel: ComputedRef< string > = computed( (): string => `${ filteredBonusReports.value.length } reports found` );

    // ------------------------------------ FILTERS ------------------------------------
    // The categories that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedCategories } = useCategoryFilter();

    // The paging filter to manage what is displayed on a per-page basis
    const { filteredContentPage } = usePagingFilter();

    // The products that have been selected to limit the results (inclusive)
    const { filterOutItemsThatDoNotContainSelectedProducts } = useProductFilter();

    // The search text entered by the user
    const { filterOutItemsThatDoNotContainSearchText } = useTextFilter();

    const filteredBonusReportsPage = computed< BonusReport[] >(() => {
        return filteredContentPage< BonusReport >( filteredBonusReports.value );
    });

    const highlightedBonusReports = computed( () => {
        let bonusReportsAfterFiltering: BonusReport[] = [];
        allBonusReports.value.forEach( ( report: BonusReport ) => {
            report.tags.forEach( ( tag: Tag ) => {
                if ( tag.slug === 'highlighted' ) {
                    bonusReportsAfterFiltering.push( report );
                }
            } );
        } );
        return bonusReportsAfterFiltering;
    } );

    const filteredBonusReports = computed< BonusReport[] >( () => {
        logger.debug( '[ bonus-report-filter ].filteredBonusReports -> starting computation.' );

        let bonusReportsAfterFiltering: BonusReport[] = [];

        if ( isAuthenticated.value ) {
            logger.debug( `[ bonus-report-filter ].filteredBonusReports -> authenticated, filtering bonus report based on short-code access.` );
            bonusReportsAfterFiltering = allBonusReports.value
                // Filter by any categories
                .filter( ( report: BonusReport ) => filterOutItemsThatDoNotContainSelectedCategories( report.categories ) )
                // Filter by any products
                .filter( ( report: BonusReport ) => filterOutItemsThatDoNotContainSelectedProducts( report.products ) )
                // Filter by text search criteria
                .filter( ( report: BonusReport ) => filterOutItemsThatDoNotContainSearchText( [ report.title ] ) );
        }

        logger.debug( `[ bonus-report-filter ].filteredBonusReports -> bonusReportsAfterFiltering : `, bonusReportsAfterFiltering );
        bonusReportsAfterFiltering.sort( ( a: BonusReport, b: BonusReport ) => a.publishedDate < b.publishedDate ? 1 : -1 );
        return bonusReportsAfterFiltering;
    } );

    return {
        // T/F based on if filteredBonusReportsPage has any items
        hasAnyBonusReportsResults,
        // '#### results found'
        searchResultsLabel,

        // The current single page sub-collection
        filteredBonusReportsPage,
        // All bonus reports that match the current filter
        filteredBonusReports,
        highlightedBonusReports
    }
}

export type BonusReportFilters = ReturnType< typeof useBonusReportFilters >;
